import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import {
  ChangeEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import LoadingButton from '@mui/lab/LoadingButton';
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import { useNavigate } from 'react-router-dom';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import {
  BaseDrawer,
  BaseDrawerTitle,
  BaseLabel,
  BaseRadioGroup,
  CustomSingleSelect,
  AvatarCombobox,
} from '@mgm/common/components/base';
import {
  CustomTextField,
} from '@mgm/common/components/form';
import { BASE_COLORS } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import {
  getCommissionPlans,
  getCommissionTeams,
  getIsOpenTeamCommissionsNav,
  getSelectedTeam,
} from '../../../../store/commissions/selectors';
import {
  TCommissionPlan, TCommissionTeam, TLocation, TMember,
} from '../../../../types';
import { commissionsActions } from '../../../../store/commissions/slice';
import { useEditTeam } from '../../hooks/useEditTeam';
import { TCreateTeamForm } from '../../../../components/form';
import { getMembers } from '../../../../store/team/selectors';
import { useCommissionPlansPermissions } from '../../hooks/useCommissionPlansPermissions';
import { useCommissions } from '../../../../hooks/useCommissions';
import { useOptions } from '../../../../hooks/useOptions';
import { useAddTeamAPI, useUpdateTeamMembersAPI, useUpdateTeamPlanAPI } from '../../../../hooks/api/useCommissions';
import { useUserLocations } from '../../../../hooks/useCommon';
import { commonActions } from '../../../../store/common/slice';

const CreateEditTeam = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const { mutate: addTeamMutate, isLoading: isAddTeamLoading } = useAddTeamAPI();
  const { mutateAsync: updateCommissionPlan, isLoading: isUpdateCommissionPlan } = useUpdateTeamPlanAPI();
  const { mutateAsync: updateCommissionMembers, isLoading: isUpdateCommissionMembers } = useUpdateTeamMembersAPI();
  const { t } = useTranslation(['commissions', 'payroll', 'team', 'inputs', 'common']);
  const isOpenTeamCommissionsNav = useAppSelector(getIsOpenTeamCommissionsNav);
  const members = useAppSelector(getMembers);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const { commissionPlansPermissions } = useCommissionPlansPermissions();
  const { createCommission } = useCommissions();
  const selectedTeam = useAppSelector(getSelectedTeam);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const navigation = useNavigate();
  const { userLocations } = useUserLocations();
  const [membersWarningMsg, setMembersWarningMsg] = useState<string>('');

  const {
    mode, setFormInputs, formInputs, isEdited,
  } = useEditTeam();

  const [selectedLocation, setSelectedLocation] = useState<TLocation>(
    formInputs?.locationTag?.id
      ? formInputs?.locationTag : {} as TLocation,
  );
  const [assignType, setAssignType] = useState(formInputs?.locationTag?.id ? '1' : '0');

  useEffect(() => {
    setAssignType(formInputs?.locationTag?.id ? '1' : '0');
    setSelectedLocation(formInputs?.locationTag?.id
      ? formInputs?.locationTag : {} as TLocation);
  }, [formInputs.locationTag]);

  const {
    commissionModelOptions,
    commissionModelMap,
    assignTypesOptions,
    calculationTypesOptions,
    stepsCalculationMap,
  } = useOptions();

  const methods = useForm<TCreateTeamForm>({
    mode: 'all',
    values: {
      teamName: formInputs.teamName,
      commissionModel: formInputs.commissionModel,
      commissionPlan: (formInputs.commissionPlan || null) as TCommissionPlan,
    },
  });

  const filteredCommissionPlans = useMemo(() => {
    if (formInputs.commissionModel?.compensation === 'team steps') {
      return commissionPlans.filter((plan) => plan.type?.toLowerCase() === 'steps');
    }

    return commissionPlans;
  }, [formInputs.commissionModel]);

  const handleCloseNav = () => {
    dispatch(commissionsActions.setIsOpenTeamCommissionsNav(false));
    setAssignType('0');
    setSelectedLocation({} as TLocation);
    setMembersWarningMsg('');

    setTimeout(() => {
      dispatch(commissionsActions.setSelectedTeam({} as TCommissionTeam));
    }, 300);
  };

  const isBtnDisabled = useMemo(() => !isEdited
    || !formInputs.members?.length || !formInputs?.commissionPlan?.id, [isEdited, formInputs]);

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';
    const isViewMode = mode === 'view';

    const actionTitle = isCreateMode ? t('addATeam', { ns: 'team' }) : t('editATeam', { ns: 'team' });

    return {
      title: isViewMode ? t('viewTeam', { ns: 'team' }) : actionTitle,
      mainBtnText: isCreateMode ? t('addTeam', { ns: 'team' }) : t('saveChanges', { ns: 'settings' }),
      isCreateMode,
      isViewMode,
    };
  }, [mode]);

  const handleFormChange = useCallback((key: string, value: unknown) => {
    setFormInputs((prevState) => {
      if (key.includes('.')) {
        const keys = key.split('.');
        const lastKey = keys.pop();

        const updateNestedObject = (obj: any, keys: string[], value: unknown): any => {
          if (!keys.length) return value;

          const [currentKey, ...remainingKeys] = keys;
          return {
            ...obj,
            [currentKey]: updateNestedObject(obj[currentKey] || {}, remainingKeys, value), // Recursively update the path
          };
        };

        return updateNestedObject(prevState, [...(keys || []), lastKey || ''], value);
      }
      return { ...prevState, [key]: value };
    });
  }, []);

  const addTeamCommission = () => {
    const members = formInputs.members.map((member) => ({
      name: member.name,
      email: member.email,
      avatarFile: member.avatarFile,
      locale: member.locale,
      shopifyId: member.shopifyId,
      commissionTags: member?.commissionTags || [],
    }));

    const newTeam = {
      teamName: formInputs.teamName,
      members,
      locationTag: selectedLocation,
      commissionPlanId: formInputs.commissionPlan?.id,
      commissionModel: formInputs.commissionModel,
    };

    addTeamMutate(newTeam);
  };

  const editTeamCommission = async (formData: TCreateTeamForm) => {
    if (!isBtnDisabled) {
      const planData = {
        teamId: selectedTeam.id,
        commissionPlanId: formData.commissionPlan?.id as number,
        ...(selectedLocation?.id && {
          locationTag: selectedLocation,
        }),
        commissionModel: formData.commissionModel,
      };

      const membersData = {
        teamId: selectedTeam.id,
        teamName: formData.teamName as string,
        members: formInputs.members,
      };

      await updateCommissionPlan(planData);
      await updateCommissionMembers(membersData);
    }
  };

  const goToCommission = () => {
    createCommission();
  };

  const filterAvailableMembers = useMemo(() => {
    const teamMembers = [...members];

    if (mode === 'create') {
      const allTeamMembers = commissionTeams.flatMap((team) => team.members.map((member) => member.email));
      return teamMembers.filter((member) => !allTeamMembers.includes(member.email));
    }

    if (mode === 'edit') {
      const currentTeamMembers = selectedTeam?.members?.map((member) => member.email) || [];

      const otherTeamMembers = commissionTeams
        .filter((team) => team.id !== selectedTeam?.id)
        .flatMap((team) => team.members.map((member) => member.email));

      return teamMembers.filter(
        (member) => currentTeamMembers.includes(member.email)
          || !otherTeamMembers.includes(member.email),
      );
    }

    return teamMembers;
  }, [mode, members, commissionTeams, selectedTeam]);

  const navigateToCommissionModel = () => {
    navigation('/commissions/models');
  };

  const onAssignRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAssignType(event.target?.value);
    if (event.target?.value === '1') {
      const teamMembers = members.filter(
        (member) => member.locations.some((location) => location.id === selectedLocation.id),
      );
      handleFormChange('members', teamMembers);
    } else {
      handleFormChange('members', []);
    }
  };

  const onLocationChanged = (localLocation: TLocation) => {
    setSelectedLocation(localLocation);

    // Get members associated with the selected location
    const locationMembers = members.filter(
      (member) => member.locations.some((location) => location.id === localLocation.id),
    );

    const conflictingMembers = commissionTeams
      .filter((team) => team.id !== selectedTeam?.id)
      .flatMap((team) => team.members)
      .filter((member) => locationMembers.some((locMember) => locMember.email === member.email));

    if (conflictingMembers.length > 0) {
      const memberNames = conflictingMembers.map((member) => member.name).join(', ');
      setMembersWarningMsg(t('theFollowingMembersAreAlready', { memberNames }));
    }

    const filteredMembers = locationMembers.filter(
      (member) => !conflictingMembers.some((conflict) => conflict.email === member.email),
    );

    handleFormChange('members', filteredMembers);
  };

  const isLocationExists = useMemo(() => {
    if (Object.keys(selectedLocation).length === 0) {
      return {
        isExists: false,
        text: '',
      };
    }

    const filteredTeams = mode === 'edit'
      ? commissionTeams.filter((team) => team.id !== selectedTeam?.id)
      : commissionTeams;

    const team = filteredTeams.find((team) => team.locationTag?.id === selectedLocation?.id);

    return {
      isExists: !!team?.id,
      text: team
        ? t('locationIsAlreadyAssigned', { location: selectedLocation.name, teamName: team.teamName })
        : '',
    };
  }, [selectedLocation, commissionTeams, mode, selectedTeam]);

  const onCommissionModelChanged = (model: string) => {
    const isTeamSteps = commissionModelMap.toKey(Number(model) as 0 | 1) === 'team steps';

    const changes = isTeamSteps
      ? {
        'commissionModel.goalsBase': 'team',
        'commissionModel.compensation': commissionModelMap.toKey(Number(model) as 0 | 1),
      }
      : {
        'commissionModel.baseLocation': {},
        'commissionModel.goalsBase': '',
        'commissionModel.compensation': commissionModelMap.toKey(Number(model) as 0 | 1),
      };

    Object.entries(changes).forEach(([key, value]) => {
      handleFormChange(key, value);
    });
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 800,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenTeamCommissionsNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={dataByMode.isCreateMode ? methods.handleSubmit(addTeamCommission) : methods.handleSubmit(editTeamCommission)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <CustomTextField
                onChange={(teamName) => {
                  handleFormChange('teamName', teamName);
                }}
                disabled={dataByMode.isViewMode}
                isRequiredField
                placeholder={t('enterTeamName')}
                labelText={t('teamName')}
                name="teamName"
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <Box>
                <BaseLabel letterSpacing="-0.5px">{t('teamMembers')}</BaseLabel>
              </Box>
              <RadioGroup
                row
                value={assignType}
                onChange={onAssignRadioChange}
              >
                {assignTypesOptions.map((radio) => (
                  <FormControlLabel
                    key={radio.id}
                    value={radio.value}
                    control={<Radio />}
                    label={radio.name}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >

              {assignType === '0'
                ? (
                  <>
                    <div className="align-center">
                      <BaseLabel letterSpacing="-0.5px">{t('selectMembers', { ns: 'payroll' })}</BaseLabel>
                      <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
                    </div>
                    <AvatarCombobox
                      isDisableMargin
                      selectedMembers={formInputs?.members as Array<TMember>}
                      members={filterAvailableMembers}
                      onChange={(members) => handleFormChange('members', members)}
                      locale={{
                        selectMembers: t('selectMembers', { ns: 'payroll' }),
                        notFound: t('notFound', { ns: 'common' }),
                        noResultsFoundFor: t('noResultsFoundFor', { ns: 'common' }),
                        tryCheckingForTypos: t('tryCheckingForTypos', { ns: 'common' }),
                      }}
                    />
                  </>
                ) : (
                  <>
                    <BaseLabel letterSpacing="-0.5px">{t('location', { ns: 'inputs' })}</BaseLabel>
                    <CustomSingleSelect
                      value={selectedLocation}
                      name="location"
                      options={userLocations}
                      onChange={(location) => onLocationChanged(location as TLocation)}
                    />
                  </>
                )}
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              {mode === 'create' && (
                <Box pt={2.5}>
                  <Button
                    fullWidth
                    startIcon={(
                      <Iconify icon="cuida:help-outline" width={20} sx={{ color: '#A855F7' }} />
                    )}
                    onClick={navigateToCommissionModel}
                    variant="outlined"
                  >
                    {t('commissionModel', { ns: 'commissions' })}
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid size={{
              sm: 12, md: 12, lg: 12, xs: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid size={{
              sm: 12, md: 12, lg: 12, xs: 12,
            }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('calculation')}
              </Typography>
            </Grid>
            <Grid
              paddingTop={0}
              size={{
                sm: 12, md: 12, lg: 12, xs: 12,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('commissionModel', { ns: 'commissions' })}</BaseLabel>
              </div>
              <BaseRadioGroup
                changeValue={(model) => onCommissionModelChanged(model)}
                value={commissionModelMap.toValue(formInputs.commissionModel.compensation)}
                isDisabled={dataByMode.isViewMode}
                label={t('commissionModel')}
                items={commissionModelOptions}
                isDisplayLabel={false}
              />
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('commissionPlan', { ns: 'commissions' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <CustomSingleSelect
                options={filteredCommissionPlans}
                maxHeight={400}
                onChange={(data) => handleFormChange('commissionPlan', data as TCommissionPlan)}
                name="commissionPlan"
                value={formInputs.commissionPlan as TCommissionPlan}
              />
              {!dataByMode.isViewMode && commissionPlansPermissions.isAllowManage && (
                <Box pt={2} display="flex" justifyContent="flex-end">
                  <Button
                    onClick={goToCommission}
                    aria-label="add new commission plan"
                  >
                    +
                    {' '}
                    {t('addANewCommission', { ns: 'commissions' })}
                  </Button>
                </Box>
              )}
            </Grid>
            {formInputs.commissionModel.compensation === 'team steps' && (
              <>
                <Grid size={{
                  xs: 12, sm: 6, md: 6, lg: 6,
                }}
                >
                  <Box>
                    <BaseLabel letterSpacing="-0.5px">{t('goalsBasedOn')}</BaseLabel>
                  </Box>
                  <RadioGroup
                    row
                    value={stepsCalculationMap.toValue(formInputs.commissionModel.goalsBase)}
                    onChange={(event) => handleFormChange(
                      'commissionModel.goalsBase',
                      stepsCalculationMap.toKey(Number(event.target?.value || 0) as 0 | 1),
                    )}
                  >
                    {calculationTypesOptions.map((radio) => (
                      <FormControlLabel
                        key={radio.id}
                        value={radio.value}
                        control={<Radio />}
                        label={radio.name}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
                {formInputs.commissionModel.goalsBase === 'location' && (
                <Grid size={{
                  xs: 12, sm: 6, md: 6, lg: 6,
                }}
                >
                  <BaseLabel letterSpacing="-0.5px">{t('location', { ns: 'inputs' })}</BaseLabel>
                  <CustomSingleSelect
                    value={formInputs.commissionModel?.baseLocation}
                    name="location"
                    options={userLocations}
                    onChange={(location) => handleFormChange(
                      'commissionModel.baseLocation',
                      location,
                    )}
                  />
                </Grid>
                )}
              </>
            )}
            {!dataByMode.isViewMode && (
              <AppBar
                position="fixed"
                color="default"
                sx={{
                  top: 'auto',
                  bottom: 0,
                  width: isMobile ? '100%' : 800,
                }}
              >
                <Toolbar sx={{
                  width: 300,
                }}
                >
                  <LoadingButton
                    aria-label="add team submit"
                    loading={isAddTeamLoading || isUpdateCommissionPlan || isUpdateCommissionMembers}
                    disabled={isBtnDisabled || isLocationExists?.isExists
                      || isAddTeamLoading || isUpdateCommissionPlan || isUpdateCommissionMembers}
                    type="submit"
                    size="large"
                    variant="contained"
                    sx={{ flexGrow: 1 }}
                  >
                    {dataByMode.mainBtnText}
                  </LoadingButton>
                </Toolbar>
              </AppBar>
            )}
          </Grid>
          {isLocationExists?.isExists && (
            <Box pt={4} pb={membersWarningMsg ? 0 : 12}>
              <Alert variant="filled" sx={{ bgcolor: '#FFC107', color: '#161C24' }} severity="info">
                {isLocationExists.text}
              </Alert>
            </Box>
          )}
          {membersWarningMsg && (
            <Box pt={4} pb={12}>
              <Alert variant="filled" sx={{ bgcolor: '#FFC107', color: '#161C24' }} severity="info">
                {membersWarningMsg}
              </Alert>
            </Box>
          )}
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CreateEditTeam,
};
